import React, { useState } from "react";
import styled from "styled-components";

import Heading from "../../UIs/Heading";
import Description from "../../UIs/Description";
import ArrowButton from "../../UIs/ArrowButton";
import "./Features.css";

const tabs = [
  {
    name: "Tasks",
    title: "Stay on Top of Every Task",
    description: "Zodot lets you create tasks, set priorities, and receive real-time reminders to keep every project on track and every deadline within reach. With an intuitive interface and smart organization tools, you can easily manage workloads and monitor progress in one place.",
    imgSrc: "/images/newhomepageFeature/Task.svg",
    link: "/features/task-management",
  },
  {
    name: "Clients & Projects",
    title: "Manage Multiple Clients & Projects Seamlessly",
    description: "Manage multiple clients and projects from a single dashboard. Gain instant visibility into project timelines, budgets, and milestones. Whether you’re handling one client or fifty, Zodot ensures transparent communication, faster approvals, and measurable progress — all in one place.",
    imgSrc: "/images/newhomepageFeature/client-project-1.svg",
    link: "/features/client-project-management",
  },
  {
    name: "Invoices",
    title: "Generate and Send Customizable Invoices With Ease",
    description:
      "Create professional, branded invoices that reflect your business identity and make a lasting impression on clients. With Zodot, you can easily generate detailed invoices, set up auto-reminders, and manage all your billing in one organized space.",
    imgSrc: "/images/newhomepageFeature/invoice.svg",
    link: "/features/invoicing",
  },
  {
    name: "Agreements",
    title: "Seal Every Deal With Branded Agreements",
    description:
      "Create, send, and e-sign branded contracts in minutes. Zodot’s built-in templates and legally compliant workflows eliminate paperwork delays. Keep every agreement tracked, timestamped, and safely stored ensuring transparency and trust with your clients and partner.",
    imgSrc: "/images/newhomepageFeature/Agreement.svg",
    link: "/features/agreements",
  },
  {
    name: "Transactions",
    title: "Keep Every Record Organized and Accessible",
    description: "With Zodot, tracking and managing your business transactions becomes effortless. Record every entry with precision, categorize expenses and income, and maintain a clear financial overview — all in one place that keeps your books clean and your business running smoothly.",
    imgSrc: "/images/newhomepageFeature/transactions.svg",
    link: "/features/transactions",
  },
  {
    name: "Statements",
    title: "Comprehensive Financial Statements",
    description:
      "Get detailed statements that summarize your earnings, expenses, and taxes in real time. Zodot automatically generates client-ready reports to help you maintain clarity, accountability, and compliance without spending hours reconciling data.",
      imgSrc: "/images/newhomepageFeature/statement.svg",
    link: "/features/statements",
  },
  {
    name: "Time tracking",
    title: "Boost Productivity With Insight-Driven Time Tracking.",
    description:
      "Track and bill every minute you work automatically. Zodot’s smart time tracker links directly with your tasks, ensuring accuracy and accountability. Visual analytics show how your time translates into profit, helping you optimize productivity across clients and projects.",
      imgSrc: "/images/newhomepageFeature/TimeTrack.svg",
    link: "/features/time-tracking",
  },
];

const TabButton = styled.button`
  width: 10.5rem;
  padding: 12px 0;
  font-weight: 500;
  cursor: pointer;
  color: var(--font1);
  border-radius: 8px;
  background-color: var(--backgroundGrey);
  transition: all 0.3s;

  &.active,
  &:hover {
    background-color: var(--brandColor);
    color: var(--white);
  }
`;

const StyledActiveTabSection = styled.div`
  display: flex;
  gap: 5rem;
  align-items: center;

  img {
    min-width: 65%;
  }

  @media (max-width: 992px) {
  flex-direction: ${(props) => (props.textFirstOnMobile ? "column-reverse" : "column")};    
  gap: 1rem;

    img {
      width: 80%;
    }
  }

  @media (max-width: 768px) {
    img {
      width: 100%;
    }
  }
`;

const SecondHeader = ({Heading: customHeading, textFirstOnMobile, showButton = true,customTitles, ...rest}) => {
  const [activeTab, setActiveTab] = useState(2);
  return (
      <div className="container-fluid py-4 py-md-5">
      <Heading data-aos="fade-up" {...rest}>
      {customHeading || "A SaaS Productivity Suite for All Your Business Needs"}
      </Heading>

      <TabList
        activeTab={activeTab}
        onSetActiveTab={(index) => setActiveTab(index)}
      />
      <ActiveTabSection 
       section={tabs[activeTab]} 
       textFirstOnMobile={textFirstOnMobile}
       showButton={showButton}
       customTitle={customTitles ? customTitles[activeTab] : null} 
      />
    </div>
  );
};

function TabList({ activeTab, onSetActiveTab}) {
  return (
    <div 
      className="d-flex flex-wrap justify-content-center gap-4 mx-auto gapBetween"
      data-aos="fade-up"
      style={{ maxWidth: "50rem" }}
    >
      {tabs.map((tab, i) => (
        <TabButton
          key={tab.name}
          onClick={() => onSetActiveTab(i)}
          className={activeTab === i ? "active" : ""}
        >
          {tab.name}
        </TabButton>
      ))}
    </div>
  );
}

function ActiveTabSection({ section,textFirstOnMobile,showButton,customTitle }) {
  const sectionName = (
    section.name === "Invoices" ? "invoicing" : section.name
  ).toLowerCase();

  return (
    <StyledActiveTabSection textFirstOnMobile={textFirstOnMobile} showButton>
      <img
        data-aos="fade-right"
        src={section.imgSrc}
        alt={`${sectionName} section img`}
      />

      <div data-aos="fade-left">
        <Heading className="mb-4 text-lg-start">{customTitle || section.title}</Heading>
        <Description className="mb-4 text-center text-md-start">{section.description}</Description>
        {showButton && (
        <ArrowButton to={section.link}>More about {sectionName}</ArrowButton>)}
        </div>
    </StyledActiveTabSection>
  );
}

export default SecondHeader;
